import { Calendar, Grid3x3, Inbox, Search, Settings, Sparkles, Plus, useTheme } from "@app/ui";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { RecurringConfirmDialog } from "../features/calendar-view/RecurringConfirmDialog.js";
import { CompletionFireworks } from "../features/completion-celebration/CompletionFireworks.js";
import { RollupConfirmDialog } from "../features/subtasks/RollupConfirmDialog.js";
import { TaskFormDialog } from "../features/task-form/TaskFormDialog.js";
import { BackupConfirmDialog, BackupImportDialog } from "../features/backup/index.js";
import { IcsImportDialog } from "../features/ics/index.js";
import { ThemeConflictDialog } from "../features/theme/ThemeConflictDialog.js";
import { FilterSidebar } from "../features/sidebar/FilterSidebar.js";
import { FeedbackButton, FeedbackDialog, useScreenshotFeedbackPrompt } from "../features/feedback/index.js";
import { SidebarContextMenu } from "../features/sidebar/SidebarContextMenu.js";
import { CollapsedBrand, SidebarResizeHandle, SidebarToggle } from "../features/sidebar/SidebarChrome.js";
import { useSidebarReorderProps } from "../features/sidebar/useSidebarReorderProps.js";
import { ShortcutsHelp } from "../features/shortcuts/index.js";
import { OnboardingScreen } from "../features/onboarding/index.js";
import { Toaster } from "../features/toast/index.js";
import { BottomNav, BlockMoveGhost, InboxDragGhost, MobileSidebar, MobileTaskSheet, SheetMorph } from "../features/mobile/index.js";
import { useMobileUx } from "../lib/useMobileUx.js";
import { useDisableMobileContextMenu } from "../lib/mobile-context-menu.js";
import { useReminderReconciler, useNotificationActions } from "../features/reminders/index.js";
import { useWidgetSnapshotSync, useWidgetDeepLinks } from "../features/widgets/index.js";
import { useSyncReconciler } from "../features/sync/index.js";
import { useGoogleSyncReconciler } from "../features/google-sync/useGoogleSyncReconciler.js";
import { AnimatePresence, Reorder, motion, useReducedMotion, type Transition } from "../lib/motion/index.js";
import { useDragClickGuard } from "../lib/useDragClickGuard.js";
import { useAndroidBackButton } from "../lib/useAndroidBackButton.js";
import { useGlobalKeyboardShortcuts } from "../lib/useGlobalKeyboardShortcuts.js";
import { localeForLanguage, type I18nKey } from "../lib/language.js";
import { useI18n } from "../lib/useI18n.js";
import { sidebarPartVisible } from "../lib/sidebar-visibility.js";
import { useSettingsStore, useSidebarAnimStore } from "../stores/index.js";
import { hydrateAppStores } from "../lib/boot-hydrate.js";
import { SIDEBAR_WIDTH_MIN, type SidebarPart } from "../stores/settings.store.js";
import { useDynamicFavicon, CalendarDayIcon } from "../lib/useDynamicFavicon.js";
import { useOverlayScrollbars } from "../lib/useOverlayScrollbars.js";
import { getStorageFallbackError } from "../platform.js";
import { toast } from "../stores/toast.store.js";
import { getSettingsTarget } from "./settings/last-settings-page.js";
import { visibleCategories } from "./settings/nav.js";

// Horizontal padding applied per-mode in `item()` — appending a competing px-*
// class doesn't reliably override (equal specificity), and in the 68px mini
// rail px-3 left too little content box: flexbox SHRANK the icons below their
// SVG size.
const navItem = "flex items-center gap-2 rounded-md py-2 text-sm hover:bg-accent";

/** Slow, soft, no-overshoot spring for the whole-sidebar collapse/expand —
 *  deliberately calmer than the app's `springs` presets so the calendar's
 *  reflow alongside it reads as a glide, not a snap (~600ms settle). */
const SIDEBAR_SPRING: Transition = { type: "spring", stiffness: 150, damping: 26, mass: 1 };

type SidebarNavPart = Exclude<SidebarPart, "filters">;

const NAV_LABEL_KEY: Record<SidebarNavPart, I18nKey> = {
  today: "navigation.part.today",
  calendar: "navigation.part.calendar",
  tasks: "navigation.part.tasks",
  priorities: "navigation.part.priorities",
  inbox: "navigation.part.inbox",
  search: "navigation.part.search",
  claude: "navigation.part.claude",
};

export function Shell() {
  const { t } = useI18n();
  useDynamicFavicon();
  useOverlayScrollbars();
  useReminderReconciler();
  useNotificationActions();
  useWidgetSnapshotSync();
  useWidgetDeepLinks();
  useSyncReconciler();
  useGoogleSyncReconciler();
  useAndroidBackButton();
  useDisableMobileContextMenu();
  useScreenshotFeedbackPrompt();
  // The entity stores are hydrated before React mounts (see `hydrateAppStores`);
  // this call is the safety net for the mounts that skip `bootstrap()` — tests,
  // the ghost-demo iframe — and is a no-op once they are loaded.
  useEffect(() => { void hydrateAppStores(); }, []);
  // The persisted theme lives in settings; mirror it into the ThemeProvider so a
  // reload (and the Settings toggle) both drive the resolved light/dark class.
  const theme = useSettingsStore((s) => s.theme);
  const { setMode } = useTheme();
  useEffect(() => { setMode(theme); }, [theme, setMode]);
  const language = useSettingsStore((s) => s.language);
  useEffect(() => { document.documentElement.lang = localeForLanguage(language); }, [language]);
  // The native store failed to open and we fell back to an empty one. Without
  // this the app is indistinguishable from a fresh install, and anything the
  // user re-creates lands in a store the next healthy boot won't show. Sticky
  // (duration 0) — this must not scroll away before it's read.
  const fallbackShown = useRef(false);
  useEffect(() => {
    if (fallbackShown.current || !getStorageFallbackError()) return;
    fallbackShown.current = true;
    toast.error(t("storage.fallback.warning"), 0);
  }, [t]);
  // The Calendar tab returns to the sub-view the user last opened (month/week/day).
  const lastCalendarView = useSettingsStore((s) => s.lastCalendarView);
  const developerMode = useSettingsStore((s) => s.developerMode);
  const sidebarVisible = useSettingsStore((s) => s.sidebarVisible);
  const matrixScope = useSettingsStore((s) => s.matrixScope);
  const prioritiesOverride = useSettingsStore((s) => s.prioritiesNavVisibleOverride);
  const sidebarNavOrder = useSettingsStore((s) => s.sidebarNavOrder);
  const setSidebarNavOrder = useSettingsStore((s) => s.setSidebarNavOrder);
  const sidebarOpen = useSettingsStore((s) => s.sidebarOpen);
  const sidebarWidth = useSettingsStore((s) => s.sidebarWidth);
  const [resizing, setResizing] = useState(false);
  const reduce = useReducedMotion();
  const prevOpen = useRef(sidebarOpen);
  const resizingRef = useRef(false);
  // On a collapse/expand toggle, flag the gesture (see sidebarAnim.store)
  // BEFORE framer's first frame so the calendar's per-column px measures don't
  // chase the per-frame reflow. Cleared by onAnimationComplete, with a timeout
  // fallback so a lost callback can't hold the measure-skip forever.
  useLayoutEffect(() => {
    if (prevOpen.current === sidebarOpen) return;
    prevOpen.current = sidebarOpen;
    useSidebarAnimStore.getState().begin();
    const t = setTimeout(() => { if (!resizingRef.current) useSidebarAnimStore.getState().end(); }, 1500);
    return () => clearTimeout(t);
  }, [sidebarOpen]);
  // Border drag: same flag for the drag's duration, one settle on release. The
  // ref guards onAnimationComplete (framer fires it per zero-duration width
  // tick during the drag) from ending the hold early.
  const onResizeDrag = useCallback((dragging: boolean) => {
    resizingRef.current = dragging;
    setResizing(dragging);
    const anim = useSidebarAnimStore.getState();
    if (dragging) anim.begin(); else anim.end();
  }, []);
  const location = useLocation();
  const onCalendar = location.pathname.startsWith("/calendar");
  const [helpOpen, setHelpOpen] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ part: SidebarPart; label: string; x: number; y: number } | null>(null);
  const showHelp = useCallback(() => setHelpOpen(true), []);
  useGlobalKeyboardShortcuts(showHelp);
  // Icon-only rail: the border was dragged below the regular minimum width.
  const mini = sidebarWidth < SIDEBAR_WIDTH_MIN;
  const item = (active: boolean) => `${navItem} ${mini ? "justify-center px-1" : "px-3"} ${active ? "bg-accent" : ""}`;
  // Icon-only rail: icons 40% larger (16px → 22px; brand 28px → 39px). Sized
  // via lucide's `size` attribute, not a Tailwind arbitrary class — the px
  // value lands as SVG width/height, no JIT-generated CSS involved.
  const iconCls = mini ? "" : "h-4 w-4";
  const iconSize = mini ? 22 : 16;
  const partVisible = (part: SidebarPart) => sidebarPartVisible(part, {
    visible: sidebarVisible,
    matrixScope,
    prioritiesOverride,
  });
  const navParts = sidebarNavOrder.filter((p): p is SidebarNavPart => p !== "filters" && partVisible(p));
  const settingsTarget = getSettingsTarget(visibleCategories(developerMode).map((c) => c.route));
  const navGuard = useDragClickGuard();
  const reorderProps = useSidebarReorderProps();

  function mergeNavOrder(nextVisible: SidebarNavPart[]): SidebarPart[] {
    const pending = [...nextVisible];
    return sidebarNavOrder.map((part) => (
      part !== "filters" && partVisible(part) ? pending.shift() ?? part : part
    ));
  }

  function renderNavIcon(part: SidebarNavPart): ReactNode {
    if (part === "today" || part === "tasks") return <Plus className={iconCls} size={iconSize} />;
    if (part === "calendar") return <Calendar className={iconCls} size={iconSize} />;
    if (part === "priorities") return <Grid3x3 className={iconCls} size={iconSize} />;
    if (part === "inbox") return <Inbox className={iconCls} size={iconSize} />;
    if (part === "search") return <Search className={iconCls} size={iconSize} />;
    return <Sparkles className={iconCls} size={iconSize} />;
  }

  function navTarget(part: SidebarNavPart): string {
    if (part === "calendar") return `/calendar/${lastCalendarView}`;
    if (part === "tasks") return "/tasks";
    if (part === "claude") return "/ai";
    return `/${part}`;
  }

  function navActive(part: SidebarNavPart): boolean {
    if (part === "calendar") return onCalendar;
    if (part === "claude") return location.pathname.startsWith("/ai");
    if (part === "tasks") return location.pathname.startsWith("/tasks");
    return location.pathname.startsWith(`/${part}`);
  }

  function openSidebarMenu(part: SidebarPart, label: string, e: ReactMouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ part, label, x: e.clientX, y: e.clientY });
  }
  // Mobile (Android app / phone): nav lives in a bottom bar, the sidebar is an
  // edge-swipe drawer holding only the filters, and the desktop aside is gone.
  const mobile = useMobileUx();
  return (
    <div className={`flex h-full w-full ${mobile ? "flex-col" : ""}`}>
      {!mobile && <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? sidebarWidth : 0 }}
        transition={resizing || reduce ? { duration: 0 } : SIDEBAR_SPRING}
        onAnimationComplete={() => { if (!resizingRef.current) useSidebarAnimStore.getState().end(); }}
        className={`relative shrink-0 overflow-hidden bg-card ${sidebarOpen ? "border-r" : ""}`}
      >
        {/* Inner column keeps the full width during the collapse animation so
            the content slides out instead of reflowing/wrapping. */}
        <div className="flex h-full flex-col p-3" style={{ width: sidebarWidth }}>
          <div className={`mb-4 flex ${mini ? "flex-col items-center gap-1" : "items-center gap-1 px-2"}`}>
            <Link to="/today" className="flex min-w-0 items-center gap-0.5 text-base font-semibold" title="YourCalendar">
              {!mini && <span>Your</span>}
              <CalendarDayIcon size={mini ? 39 : 28} />
              {!mini && <span className="truncate">Calendar</span>}
            </Link>
            <SidebarToggle open />
          </div>
          <Reorder.Group
            as="nav"
            axis="y"
            values={navParts}
            onReorder={(next) => void setSidebarNavOrder(mergeNavOrder(next as SidebarNavPart[]))}
            className="flex flex-col gap-1"
          >
            {navParts.map((part) => {
              const label = t(NAV_LABEL_KEY[part]);
              return (
                <Reorder.Item
                  key={part}
                  as="div"
                  value={part}
                  onDragStart={navGuard.onDragStart}
                  onDragEnd={navGuard.onDragEnd}
                  className="cursor-grab touch-none active:cursor-grabbing"
                  {...reorderProps}
                >
                  <NavLink
                    to={navTarget(part)}
                    title={label}
                    draggable={false}
                    onClick={(e) => { if (navGuard.suppressClick()) e.preventDefault(); }}
                    onContextMenu={(e) => openSidebarMenu(part, label, e)}
                    className={item(navActive(part))}
                  >
                    {renderNavIcon(part)} {!mini && label}
                  </NavLink>
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
          <motion.div layoutScroll className="mt-3 min-h-0 flex-1 space-y-2 overflow-y-auto">
            {sidebarVisible.filters && !mini && <FilterSidebar onContextMenu={(e) => openSidebarMenu("filters", t("common.filters"), e)} />}
          </motion.div>
          <div className="mt-3 flex shrink-0 flex-col gap-1">
            <FeedbackButton mini={mini} />
            <NavLink to={settingsTarget} title={t("common.settings")} className={({ isActive }) => item(isActive)}>
              <Settings className={iconCls} size={iconSize} /> {!mini && t("common.settings")}
            </NavLink>
          </div>
        </div>
        {sidebarOpen && <SidebarResizeHandle onDraggingChange={onResizeDrag} />}
      </motion.aside>}
      <main
        className={`flex min-h-0 min-w-0 flex-1 flex-col ${mobile ? (onCalendar ? "" : "py-2") : "p-3 sm:p-6"}`}
        // Mobile: honour left/right safe areas (rounded corners / notches) with a
        // sensible min so content never sits flush against the screen edge — EXCEPT
        // the calendar, which the mobile design takes edge-to-edge (no padding).
        style={mobile && !onCalendar ? { paddingLeft: "max(12px, env(safe-area-inset-left))", paddingRight: "max(12px, env(safe-area-inset-right))" } : undefined}
      >
        {/* Calendar routes show the brand INSIDE the CalendarHeader instead,
            so the grid below keeps the full width. */}
        <AnimatePresence initial={false}>
          {!mobile && !sidebarOpen && !onCalendar && <CollapsedBrand key="brand" vertical className="mb-3" />}
        </AnimatePresence>
        <div className="min-h-0 min-w-0 flex-1 overflow-auto"><Outlet /></div>
      </main>
      {mobile && <BottomNav />}
      {mobile && <MobileSidebar />}
      {mobile && <InboxDragGhost />}
      {mobile && <BlockMoveGhost />}
      {mobile ? <MobileTaskSheet /> : <TaskFormDialog />}
      {mobile && <SheetMorph />}
      <RecurringConfirmDialog />
      <RollupConfirmDialog />
      <OnboardingScreen />
      <BackupConfirmDialog />
      <BackupImportDialog />
      <IcsImportDialog />
      <ThemeConflictDialog />
      <CompletionFireworks />
      <FeedbackDialog />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      {ctxMenu && (
        <SidebarContextMenu
          part={ctxMenu.part}
          label={ctxMenu.label}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
        />
      )}
      <Toaster />
    </div>
  );
}
